/**
 * Location Provider Factory
 *
 * Returns the configured location provider based on environment variable.
 * Defaults to Radar.
 */

import * as radarProvider from './radarProvider';

// Provider implementations
const providers = {
  radar: radarProvider,
  // Future: google, etc.
};

/**
 * Get the configured provider name
 * @returns {string}
 */
function getProviderName() {
  return process.env.REACT_APP_LOCATION_PROVIDER || 'radar';
}

/**
 * Get the current location provider
 * @returns {Object} Provider with getLocation() function
 */
export function getProvider() {
  const name = getProviderName();
  const provider = providers[name];

  if (!provider) {
    console.warn(`[LOCATION] Unknown provider "${name}", falling back to radar`);
    return providers.radar;
  }

  return provider;
}

/**
 * Get location using the configured provider
 * @param {number} latitude
 * @param {number} longitude
 * @returns {Promise<import('./types').StandardLocationResponse>}
 */
export async function getLocation(latitude, longitude) {
  const provider = getProvider();
  return provider.getLocation(latitude, longitude);
}

/**
 * Forward search using the configured provider (autocomplete).
 * @param {string} query
 * @param {number} [lat]
 * @param {number} [lng]
 * @param {string} [layers] - provider layers filter, e.g. 'locality,neighborhood'
 * @returns {Promise<Array>}
 */
export async function searchAutocomplete(query, lat, lng, layers) {
  const provider = getProvider();
  if (!provider.searchAutocomplete) return [];
  return provider.searchAutocomplete(query, lat, lng, layers);
}

/**
 * Reverse geocode coordinates to an address (city/country/neighborhood) using
 * the configured provider. Address-only (no venue search).
 * @param {number} latitude
 * @param {number} longitude
 * @returns {Promise<Object|null>}
 */
export async function reverseGeocode(latitude, longitude) {
  const provider = getProvider();
  if (!provider.reverseGeocode) return null;
  return provider.reverseGeocode(latitude, longitude);
}

/**
 * Clear the location cache (delegates to current provider)
 */
export function clearCache() {
  const provider = getProvider();
  if (provider.clearCache) {
    provider.clearCache();
  }
}

// Re-export types for convenience
export * from './types';
